import { Link, Typography } from "@mui/material";
import {
  ErrorPanel,
  ASTableV2,
  ASTableV2Header,
  ASTableV2Row,
  ASTableV2Cell,
  ASTableV2Body,
  ASTableV2RowSkeleton,
  ASTableV2RowEmpty,
  globalDateTimeFormat,
  RelativeTime
} from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import dayjs from "dayjs";
import { t } from "i18next";
import { useMemo } from "react";

import { useAppSelector } from "~/app/store";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { useGetUnifyPortErrorsQuery } from "~/redux/warehouse/unify.openApi";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

import {
  buildPortIdToWorkstationMap,
  getStatusLookupUrl,
  getWorkstationLabel
} from "./unify.utils";

type UnifyPortErrorsProps = {
  requestedGridId?: string;
  requestedWorkstationId?: string;
};

export const UnifyPortErrors = ({
  requestedGridId,
  requestedWorkstationId
}: UnifyPortErrorsProps) => {
  const selectedGridId = useAppSelector(
    (state) => state.serviceSupport.selectedGridId
  );
  const selectedWorkstationId = useAppSelector(
    (state) => state.serviceSupport.selectedWorkstationId
  );
  const workstationId = requestedWorkstationId || selectedWorkstationId;
  const autostoreGridId = requestedGridId || selectedGridId;

  const { data: workstations } = useGetWorkstationsQuery();
  const portIdToWorkstation = useMemo(
    () => buildPortIdToWorkstationMap(workstations, autostoreGridId),
    [workstations, autostoreGridId]
  );

  const {
    data: portErrors,
    isFetching: isUnifyFetching,
    error: unifyFetchError,
    refetch: refetchPortErrors
  } = useGetUnifyPortErrorsQuery(
    autostoreGridId
      ? {
          autostoreGridId,
          workstationId,
          limit: null
        }
      : skipToken
  );

  return (
    <>
      {unifyFetchError && (
        <ErrorPanel
          message={getMessageFromRtkError(unifyFetchError)}
          tryAgain={{
            onTryAgain: async () => {
              await refetchPortErrors();
            }
          }}
        />
      )}
      {!unifyFetchError && (
        <ASTableV2 gridTemplateColumns={"repeat(6, auto)"} isLoading={false}>
          <ASTableV2Header>
            <ASTableV2Row>
              <ASTableV2Cell>
                <Typography variant="tableBody">
                  {t("date and time")}
                </Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("workstation")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("port id")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("error code")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">
                  {t("error description")}
                </Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("status")}</Typography>
              </ASTableV2Cell>
            </ASTableV2Row>
          </ASTableV2Header>
          <ASTableV2Body>
            {isUnifyFetching && <ASTableV2RowSkeleton columns={6} rows={1} />}
            {!isUnifyFetching && !portErrors?.length && (
              <ASTableV2RowEmpty>{t("no results")}</ASTableV2RowEmpty>
            )}
            {!isUnifyFetching &&
              portErrors
                ?.toSorted((a, b) =>
                  dayjs(a.item.timestamp).isBefore(dayjs(b.item.timestamp))
                    ? 1
                    : -1
                )
                .map((row, i) => {
                  const statusLookupUrl = getStatusLookupUrl(
                    row.item.portErrorCode,
                    row.item.portError
                  );
                  return (
                    <ASTableV2Row key={i}>
                      <ASTableV2Cell
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start"
                        }}
                      >
                        <RelativeTime
                          time={row.item.timestamp}
                          variant="tableBody"
                        />
                        <Typography>
                          {dayjs(row.item.timestamp).format(
                            globalDateTimeFormat
                          )}
                        </Typography>
                      </ASTableV2Cell>
                      <ASTableV2Cell>
                        <Typography variant="tableBody">
                          {getWorkstationLabel(
                            portIdToWorkstation,
                            row.item.portId
                          )}
                        </Typography>
                      </ASTableV2Cell>
                      <ASTableV2Cell>
                        <Typography variant="tableBody">
                          {row.item.portId}
                        </Typography>
                      </ASTableV2Cell>
                      <ASTableV2Cell>
                        <Typography variant="tableBody">
                          {row.item.portErrorCode}
                        </Typography>
                      </ASTableV2Cell>
                      <ASTableV2Cell>
                        {statusLookupUrl ? (
                          <Link
                            href={statusLookupUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="tableBody"
                          >
                            {row.item.portError}
                          </Link>
                        ) : (
                          <Typography variant="tableBody">
                            {row.item.portError}
                          </Typography>
                        )}
                      </ASTableV2Cell>
                      <ASTableV2Cell>
                        <Typography variant="tableBody">
                          {row.item.status}
                        </Typography>
                      </ASTableV2Cell>
                    </ASTableV2Row>
                  );
                })}
          </ASTableV2Body>
        </ASTableV2>
      )}
    </>
  );
};
